import { api as defaultApi, type ApiClient } from './client';
import type {
  CabinetCase,
  CabinetFeedback,
  CabinetFeedbackItem,
  FamilySteps,
  FeedbackAnswer,
  I18nBundle,
  NotificationsPage,
  PublicFeedback,
  CabinetCaseDetail,
  ChatMessage,
  Me,
  OtpRequestResponse,
  OtpVerifyResponse,
  Page,
  PublicSite,
  UploadUrlResponse,
} from './types';

const enc = encodeURIComponent;

/** Lists may come as a bare array or as a page `{items, next_cursor}`; accept both. */
export function asList<T>(v: T[] | Page<T> | null | undefined): T[] {
  if (!v) return [];
  return Array.isArray(v) ? v : Array.isArray(v.items) ? v.items : [];
}

export function cabinetApi(c: ApiClient = defaultApi) {
  return {
    requestCode: (destination: string) =>
      c.post<OtpRequestResponse>('/cabinet/auth/request', { destination }, { skipAuthRedirect: true }),
    verifyCode: (destination: string, code: string) =>
      c.post<OtpVerifyResponse>('/cabinet/auth/verify', { destination, code }, { skipAuthRedirect: true }),
    me: () => c.get<Me>('/auth/me', { skipAuthRedirect: true }),
    logout: () => c.post<{ ok: boolean }>('/auth/logout', {}, { skipAuthRedirect: true }),
    site: () => c.get<PublicSite>('/public/site', { skipAuthRedirect: true }),

    cases: async () => asList(await c.get<CabinetCase[] | Page<CabinetCase>>('/cabinet/cases')),
    caseDetail: async (id: string) => {
      const d = await c.get<CabinetCaseDetail>(`/cabinet/cases/${enc(id)}`);
      return { ...d, checklist: asList(d.checklist), events: asList(d.events), documents: asList(d.documents) };
    },
    uploadUrl: (caseId: string, body: { document_type_key?: string; checklist_item_id?: string; file_name: string; mime: string; size: number }) =>
      c.post<UploadUrlResponse>(`/cabinet/cases/${enc(caseId)}/documents/upload-url`, body),
    completeUpload: (documentId: string) => c.post<unknown>(`/cabinet/documents/${enc(documentId)}/complete`, {}),
    downloadUrl: (documentId: string) => c.get<{ url: string }>(`/cabinet/documents/${enc(documentId)}/download-url`),
    /** `after` (newest id we have) is a backend extension: only newer messages are returned. */
    messages: async (caseId: string, o: { signal?: AbortSignal; after?: string } = {}) =>
      asList(
        await c.get<ChatMessage[] | Page<ChatMessage>>(`/cabinet/cases/${enc(caseId)}/messages${o.after ? `?after=${enc(o.after)}` : ''}`, {
          signal: o.signal,
        }),
      ),
    sendMessage: (caseId: string, body: string) => c.post<ChatMessage>(`/cabinet/cases/${enc(caseId)}/messages`, { body }),
    exportData: () => c.get<unknown>('/cabinet/export'),

    // --- batch: p5-C
    steps: (caseId: string) => c.get<FamilySteps>(`/cabinet/cases/${enc(caseId)}/steps`),
    notifications: (o: { cursor?: string | null; limit?: number } = {}) => {
      const q = new URLSearchParams();
      if (o.limit) q.set('limit', String(o.limit));
      if (o.cursor) q.set('cursor', o.cursor);
      const qs = q.toString();
      return c.get<NotificationsPage>(`/cabinet/notifications${qs ? `?${qs}` : ''}`);
    },
    markNotificationsRead: (ids?: string[]) =>
      c.post<{ updated: number }>('/cabinet/notifications/read', ids?.length ? { ids } : { all: true }),
    feedback: async (): Promise<CabinetFeedback> => {
      const r = await c.get<CabinetFeedback | CabinetFeedbackItem[]>('/cabinet/feedback');
      return Array.isArray(r) ? { items: r } : { ...r, items: asList(r?.items) };
    },
    answerFeedback: (caseId: string, body: { rating: number; text?: string }) =>
      c.post<{ ok: boolean; status?: string }>(`/cabinet/feedback/${enc(caseId)}`, body),
    feedbackOptOut: (optOut: boolean) => c.post<{ ok?: boolean; opt_out?: boolean }>('/cabinet/feedback/opt-out', { opt_out: optOut }),
    publicFeedback: (token: string) => c.get<PublicFeedback>(`/public/feedback/${enc(token)}`, { skipAuthRedirect: true }),
    answerPublicFeedback: (token: string, body: FeedbackAnswer) =>
      c.post<{ ok: boolean; status: string }>(`/public/feedback/${enc(token)}`, body, { skipAuthRedirect: true }),
    i18n: (namespace = 'cabinet') => c.get<I18nBundle>(`/i18n/${enc(namespace)}`, { skipAuthRedirect: true }),
  };
}

export type CabinetApi = ReturnType<typeof cabinetApi>;
export const cabinet = cabinetApi();
